import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { DuplicateIconComponent, EditIconComponent } from '@kuiper/icons';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { CustomWorkflowService } from '../custom-workflow.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import {
  CustomWorkflow,
  CustomWorkflowApiError,
  CustomWorkflowStep,
  DrawerKind,
  FieldAccess,
  PublishError,
  PublishErrorCode,
} from '../custom-workflow.model';
import { CustomWorkflowStepEditorComponent } from '../custom-workflow-step-editor/custom-workflow-step-editor.component';

type DrawerState = { kind: DrawerKind.Step; stepId: number } | null;

@Component({
  selector: 'lib-custom-workflow-canvas-tab',
  imports: [
    TranslocoPipe,
    CdkDropList,
    CdkDrag,
    CustomWorkflowStepEditorComponent,
    EditIconComponent,
    DuplicateIconComponent,
    BadgeComponent,
  ],
  templateUrl: './custom-workflow-canvas-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomWorkflowCanvasTabComponent {
  private readonly service = inject(CustomWorkflowService);
  private readonly transloco = inject(TranslocoService);
  private readonly modalService = inject(ModalServiceService);
  /** The workflow.reflections flag: off hides everything reflection-related, on custom and built-in workflows alike. */
  protected readonly reflectionsEnabled = signal(
    inject(FeatureFlagsService).getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOW_CONFIGURATION.reflections
    )
  ).asReadonly();

  readonly discardTemplate = viewChild<TemplateRef<unknown>>('discard');
  readonly activeEditor = viewChild<{ dirty(): boolean }>('editor');
  readonly canvasBody = viewChild<ElementRef<HTMLElement>>('canvasBody');

  private readonly ZOOM_MIN = 0.25;
  private readonly ZOOM_MAX = 2.0;
  private readonly ZOOM_STEP = 0.1;
  readonly DrawerKind = DrawerKind;

  workflow = input.required<CustomWorkflow>();
  readonly = input<boolean>(false);
  publishErrors = input<PublishError[]>([]);
  changed = output<void>();
  apiError = output<CustomWorkflowApiError>();

  drawer = signal<DrawerState>(null);
  zoomScale = signal(1.0);
  reorderingStepId = signal<number | null>(null);

  steps = computed(() => this.workflow().steps ?? []);
  initiation = computed(() => this.steps().find(s => s.is_initiation) ?? null);
  movableSteps = computed(() => this.steps().filter(s => !s.is_initiation));
  selectedStep = computed(() => {
    const d = this.drawer();
    return d?.kind === DrawerKind.Step
      ? (this.steps().find(s => s.id === d.stepId) ?? null)
      : null;
  });
  zoomPercent = computed(() => `${Math.round(this.zoomScale() * 100)}%`);
  selectedStepId = computed(() => this.selectedStep()?.id ?? null);
  selectedStepTitle = computed(() => this.selectedStep()?.name ?? '');
  initiationCard = computed(() => {
    const step = this.initiation();
    return step
      ? {
          step,
          title: step.name,
          reflectionsCount: this.reflectionsCount(step),
        }
      : null;
  });
  stepCards = computed(() =>
    this.movableSteps().map(step => ({
      step,
      title: step.name,
      approver: this.approverSummary(step),
      reflectionsCount: this.reflectionsCount(step),
      hasError: this.hasError(PublishErrorCode.StepWithoutApprover, step.id),
    }))
  );
  boxErrors = computed(() => ({
    noSteps: this.hasError(PublishErrorCode.NoSteps),
  }));

  private reflectionsCount(step: CustomWorkflowStep): number {
    return this.reflectionsEnabled() ? (step.reflections_count ?? 0) : 0;
  }

  hasError(code: PublishErrorCode, stepId?: number): boolean {
    return this.publishErrors().some(
      e => e.code === code && (stepId === undefined || e.step_id === stepId)
    );
  }

  private approverSummary(step: CustomWorkflowStep): string {
    if (!step.assignees.length)
      return this.transloco.translate('custom_workflow_no_approver');
    const first = step.assignees[0].data?.name ?? '';
    return step.assignees.length > 1
      ? `${first} +${step.assignees.length - 1}`
      : first;
  }

  nextPosition(): number {
    return this.steps().length + 1;
  }

  zoomIn() {
    this.zoomScale.update(s =>
      Math.min(this.ZOOM_MAX, Math.round((s + this.ZOOM_STEP) * 10) / 10)
    );
  }

  zoomOut() {
    this.zoomScale.update(s =>
      Math.max(this.ZOOM_MIN, Math.round((s - this.ZOOM_STEP) * 10) / 10)
    );
  }

  resetZoom() {
    this.zoomScale.set(1.0);
  }

  fitToScreen() {
    const el = this.canvasBody()?.nativeElement;
    if (!el) {
      this.zoomScale.set(1.0);
      return;
    }
    const contentEl = el.querySelector('.inline-flex') as HTMLElement | null;
    if (!contentEl) {
      this.zoomScale.set(1.0);
      return;
    }
    const scaleW = (el.clientWidth - 96) / contentEl.scrollWidth;
    const scaleH = (el.clientHeight - 120) / contentEl.scrollHeight;
    const scale = Math.min(scaleW, scaleH, 1.0);
    this.zoomScale.set(Math.max(this.ZOOM_MIN, Math.round(scale * 10) / 10));
  }

  open(stepId: number) {
    this.drawer.set({ kind: DrawerKind.Step, stepId });
  }

  requestClose() {
    if (this.activeEditor()?.dirty()) {
      this.modalService.modalContent.set({
        isOpen: true,
        hasCloseIcon: false,
        title: '',
        customizeContent: this.discardTemplate(),
      });
      return;
    }
    this.drawer.set(null);
  }

  discardAndClose() {
    this.modalService.modalContent.set({ isOpen: false });
    this.drawer.set(null);
  }

  keepEditing() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  addStep(position: number) {
    const wf = this.workflow();
    const name = this.transloco.translate('custom_workflow_new_step_name');
    this.service
      .createStep(wf.id, { name: { en: name, ar: name }, position })
      .subscribe({
        next: res => {
          this.changed.emit();
          this.drawer.set({ kind: DrawerKind.Step, stepId: res.data.id });
        },
        error: e => this.apiError.emit(e),
      });
  }

  duplicateStep(stepId: number) {
    const wf = this.workflow();
    const step = this.steps().find(s => s.id === stepId);
    if (!step) return;
    const name = {
      en: step.translations.name?.en ?? step.name,
      ar: step.translations.name?.ar ?? step.name,
    };
    this.service
      .createStep(wf.id, { name, position: step.position + 1 })
      .subscribe({
        next: res => {
          const fieldAccess: Record<string, FieldAccess> = {};
          step.editable_fields.forEach(
            key => (fieldAccess[key] = FieldAccess.Edit)
          );
          step.hidden_fields.forEach(
            key => (fieldAccess[key] = FieldAccess.Hidden)
          );
          if (!Object.keys(fieldAccess).length) {
            this.changed.emit();
            return;
          }
          this.service
            .updateStep(wf.id, res.data.id, { field_access: fieldAccess })
            .subscribe({
              next: () => this.changed.emit(),
              error: e => this.apiError.emit(e),
            });
        },
        error: e => this.apiError.emit(e),
      });
  }

  dropStep(
    event: Pick<
      CdkDragDrop<CustomWorkflowStep[]>,
      'previousIndex' | 'currentIndex'
    >
  ) {
    if (event.previousIndex === event.currentIndex) return;
    const wf = this.workflow();
    const moved = this.movableSteps()[event.previousIndex];
    const list = [...this.movableSteps()];
    moveItemInArray(list, event.previousIndex, event.currentIndex);
    this.reorderingStepId.set(moved.id);
    this.service
      .reorderSteps(
        wf.id,
        list.map(s => s.id)
      )
      .subscribe({
        next: () => {
          this.reorderingStepId.set(null);
          this.changed.emit();
        },
        error: e => {
          this.reorderingStepId.set(null);
          this.apiError.emit(e);
          if (!e?.locked) this.changed.emit();
        },
      });
  }
}
